import React, { useState, useMemo } from 'react';
import {
  WizardWorkspace,
  VerticalStack,
  HorizontalStack,
  CommerceObjectSelector,
  CommerceObjectMultiSelector,
  DescriptionText,
  PropertyGrid,
  PropertyItem,
  Surface,
  SectionSeparator,
} from '@workspace/silk-ui';
import {
  Info,
  Layers,
  Map as MapIcon,
  Store,
  Tag,
  Settings2,
  Database,
  Users,
  Compass,
  DollarSign,
} from 'lucide-react';
import type { CommerceSelectOptionViewModel } from '@workspace/silk-ui';
import { ZoneMapEditor } from './ZoneMapEditor';
import type { ZoneInfrastructureBinding } from './zoneInfrastructure';
import {
  buildMinimumBuildCostBandLabel,
  type RoutePricingBuildTypeRateFormValue,
} from '@/features/commerce-admin/routePricingRateController';

// ─── Types ────────────────────────────────────────────────────────────────────

export type WorkspaceEditorKind = 'zone' | 'market' | 'group';

export type ZoneFormState = {
  zoneKey: string;
  name: string;
  zoneKind: string;
  sourceSystem: string;
  sourceRef: string;
  buildTypeRates: RoutePricingBuildTypeRateFormValue[];
  metadataJSON: string;
};

export type MarketFormState = {
  zoneId: string;
  marketKey: string;
  name: string;
  status: string;
  installMethod: string;
  canSignUp: boolean;
  canScheduleInstall: boolean;
  estimatedReadyAt: string;
  customerMessage: string;
  marketGroupId: string;
  metadataJSON: string;
};

export type GroupFormState = {
  marketGroupKey: string;
  name: string;
  marketIds: string[];
  marketLabels?: string[];
  metadataJSON: string;
};

export interface MarketsWizardEditorProps {
  isOpen: boolean;
  kind: WorkspaceEditorKind;
  editingId: string | null;
  zoneForm: ZoneFormState;
  marketForm: MarketFormState;
  groupForm: GroupFormState;
  setZoneForm: React.Dispatch<React.SetStateAction<ZoneFormState>>;
  setMarketForm: React.Dispatch<React.SetStateAction<MarketFormState>>;
  setGroupForm: React.Dispatch<React.SetStateAction<GroupFormState>>;
  onSave: () => Promise<void>;
  onClose: () => void;
  saving: boolean;
  zoneOptions: CommerceSelectOptionViewModel[];
  marketOptions: CommerceSelectOptionViewModel[];
  marketGroupOptions: CommerceSelectOptionViewModel[];
  zoneInfrastructureBindings: ZoneInfrastructureBinding[];
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FormField({ label, children, htmlFor }: { label: string; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';
const selectCls = `${inputCls} cursor-pointer`;
const textareaCls = 'min-h-32 w-full rounded-lg border border-border/60 bg-background px-4 py-3 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function CheckToggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/50 bg-background px-3.5 py-3 transition-colors hover:bg-muted/10">
      <div className="mt-0.5 flex-shrink-0">
        <div
          onClick={() => onChange(!checked)}
          className={`flex h-4 w-4 items-center justify-center rounded border-2 transition-colors ${
            checked ? 'border-primary bg-primary' : 'border-border/60 bg-background'
          }`}
        >
          {checked && (
            <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-none stroke-primary-foreground stroke-[2]">
              <polyline points="1,4 3,6 7,2" />
            </svg>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-semibold text-foreground leading-tight">{label}</span>
        {description && <span className="text-xs text-muted-foreground leading-relaxed">{description}</span>}
      </div>
    </label>
  );
}

function RateRows({
  rates,
  onChange,
  addLabel,
}: {
  rates: RoutePricingBuildTypeRateFormValue[];
  onChange: (next: RoutePricingBuildTypeRateFormValue[]) => void;
  addLabel: string;
}) {
  return (
    <VerticalStack gap={3}>
      {rates.map((rate, index) => (
        <div key={rate.rowId}>
          <HorizontalStack gap={3} className="grid grid-cols-1 items-end sm:grid-cols-[1fr_8rem_1fr_1fr_auto]">
            <FormField htmlFor={`${rate.rowId}-build-type`} label={index === 0 ? 'Build Type' : `Build Type ${index + 1}`}>
              <input
                id={`${rate.rowId}-build-type`}
              className={inputCls}
              value={rate.buildType}
              onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, buildType: event.target.value } : current)))}
              placeholder="aerial"
            />
          </FormField>
            <FormField htmlFor={`${rate.rowId}-currency`} label={index === 0 ? 'Currency' : `Currency ${index + 1}`}>
              <input id={`${rate.rowId}-currency`} className={inputCls} value={rate.currency} maxLength={3} onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, currency: event.target.value.toUpperCase() } : current)))} placeholder="USD" />
            </FormField>
            <FormField htmlFor={`${rate.rowId}-price-per-meter`} label={index === 0 ? 'Price / m' : `Price / m ${index + 1}`}>
              <input
                id={`${rate.rowId}-price-per-meter`}
                inputMode="decimal"
              className={inputCls}
              value={rate.pricePerMeter}
              onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, pricePerMeter: event.target.value } : current)))}
              placeholder="12.5"
            />
          </FormField>
            <FormField htmlFor={`${rate.rowId}-minimum-build-cost`} label={index === 0 ? 'Minimum Build Cost' : `Minimum ${index + 1}`}>
              <input id={`${rate.rowId}-minimum-build-cost`} inputMode="decimal" className={inputCls} value={rate.minimumBuildCost} onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, minimumBuildCost: event.target.value } : current)))} placeholder="0.00" />
          </FormField>
          <button
            type="button"
            aria-label={`Remove ${rate.buildType || `rate ${index + 1}`}`}
            onClick={() => onChange(rates.filter((_, currentIndex) => currentIndex !== index))}
            className="inline-flex h-10 items-center rounded-md border border-border/60 px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/30"
          >
            Remove
          </button>
        </HorizontalStack>
        {buildMinimumBuildCostBandLabel(rate) && <DescriptionText className="mt-1 text-[11px]">{buildMinimumBuildCostBandLabel(rate)}</DescriptionText>}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rates, { rowId: `route-rate-${Date.now()}`, buildType: '', currency: 'USD', pricePerMeter: '', minimumBuildCost: '' }])}
        className="inline-flex h-10 w-fit items-center gap-2 rounded-md border border-border/60 bg-background px-4 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/30"
      >
        {addLabel}
      </button>
    </VerticalStack>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function MarketsWizardEditor({
  isOpen,
  kind,
  editingId,
  zoneForm,
  marketForm,
  groupForm,
  setZoneForm,
  setMarketForm,
  setGroupForm,
  onSave,
  onClose,
  saving,
  zoneOptions,
  marketOptions,
  marketGroupOptions,
  zoneInfrastructureBindings,
}: MarketsWizardEditorProps) {
  const [activeTabId, setActiveTabId] = useState('identity');

  const tabs = useMemo(() => {
    if (kind === 'zone') {
      return [
        { id: 'identity', label: 'Identity', icon: <Info className="h-4 w-4" /> },
        { id: 'source', label: 'Source', icon: <Database className="h-4 w-4" /> },
        { id: 'geometry', label: 'Geometry', icon: <Compass className="h-4 w-4" /> },
        { id: 'pricing', label: 'Pricing', icon: <DollarSign className="h-4 w-4" /> },
        { id: 'advanced', label: 'Advanced', icon: <Settings2 className="h-4 w-4" /> },
      ];
    }
    if (kind === 'market') {
      return [
        { id: 'identity', label: 'Identity', icon: <Info className="h-4 w-4" /> },
        { id: 'readiness', label: 'Readiness', icon: <Store className="h-4 w-4" /> },
        { id: 'groups', label: 'Market Group', icon: <Users className="h-4 w-4" />, count: marketForm.marketGroupId ? 1 : 0 },
        { id: 'advanced', label: 'Advanced', icon: <Settings2 className="h-4 w-4" /> },
      ];
    }
    return [
      { id: 'identity', label: 'Identity', icon: <Info className="h-4 w-4" /> },
      { id: 'markets', label: 'Markets', icon: <Layers className="h-4 w-4" />, count: groupForm.marketIds.length },
      { id: 'advanced', label: 'Advanced', icon: <Settings2 className="h-4 w-4" /> },
    ];
  }, [kind, marketForm.marketGroupId.length, groupForm.marketIds.length]);

  const editorTitle = useMemo(() => {
    const action = editingId ? 'Edit' : 'New';
    if (kind === 'zone') return `${action} Zone`;
    if (kind === 'market') return `${action} Market`;
    return `${action} Market Group`;
  }, [kind, editingId]);

  const editorSubtitle = useMemo(() => {
    if (kind === 'zone') return 'Geography and lookup bindings for rollout markets.';
    if (kind === 'market') return 'Rollout readiness, installability, and eligibility.';
    return 'Group markets for policy and rollout targeting.';
  }, [kind]);

  const summaryItems = useMemo(() => {
    if (kind === 'zone') {
      return [
        { label: 'Name', value: zoneForm.name || 'Not set', complete: !!zoneForm.name },
        { label: 'Key', value: zoneForm.zoneKey || 'Not set', complete: !!zoneForm.zoneKey },
        { label: 'Kind', value: zoneForm.zoneKind, complete: true },
        { label: 'Source', value: zoneForm.sourceSystem || 'Manual', complete: true },
        { label: 'Pricing', value: zoneForm.buildTypeRates.length > 0 ? `${zoneForm.buildTypeRates.length} override rates` : 'Layer defaults only', complete: true },
      ];
    }
    if (kind === 'market') {
      const zoneName = zoneOptions.find(z => z.value === marketForm.zoneId)?.label || 'None';
      return [
        { label: 'Name', value: marketForm.name || 'Not set', complete: !!marketForm.name },
        { label: 'Zone', value: zoneName, complete: !!marketForm.zoneId },
        { label: 'Status', value: marketForm.status.toUpperCase(), complete: true },
        { label: 'Market group', value: marketForm.marketGroupId ? 'Assigned' : 'None', complete: true },
      ];
    }
    return [
      { label: 'Name', value: groupForm.name || 'Not set', complete: !!groupForm.name },
      { label: 'Key', value: groupForm.marketGroupKey || 'Not set', complete: !!groupForm.marketGroupKey },
      { label: 'Markets', value: `${groupForm.marketIds.length} grouped`, complete: true },
    ];
  }, [kind, zoneForm, marketForm, groupForm, zoneOptions]);

  const zoneParsedMetadata = useMemo(() => {
    try {
      return JSON.parse(zoneForm.metadataJSON) as Record<string, unknown>;
    } catch {
      return {};
    }
  }, [zoneForm.metadataJSON]);

  const groupedMarketMembers = useMemo(() => {
    const storedLabels = new Map(groupForm.marketIds.map((marketId, index) => [marketId, groupForm.marketLabels?.[index]]));
    return groupForm.marketIds.map(marketId => {
      const option = marketOptions.find(candidate => candidate.value === marketId);
      return {
        marketId,
        label: option?.label || storedLabels.get(marketId) || marketId,
        description: option?.description,
      };
    });
  }, [groupForm.marketIds, groupForm.marketLabels, marketOptions]);

  return (
    <WizardWorkspace
      isOpen={isOpen}
      title={editorTitle}
      subtitle={editorSubtitle}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={editingId ? 'Update' : 'Create'}
      summaryItems={summaryItems}
    >
      {/* ── ZONE EDITOR ────────────────────────────────────────────────────────── */}
      {kind === 'zone' && (
        <VerticalStack gap={6}>
          {activeTabId === 'identity' && (
            <VerticalStack gap={4}>
              <FormField label="Zone Name">
                <input
                  className={inputCls}
                  value={zoneForm.name}
                  onChange={e => setZoneForm(c => ({ ...c, name: e.target.value }))}
                  placeholder="e.g. Seattle Metro"
                />
              </FormField>
              <FormField label="Zone Key">
                <input
                  className={inputCls}
                  value={zoneForm.zoneKey}
                  onChange={e => setZoneForm(c => ({ ...c, zoneKey: e.target.value }))}
                  placeholder="e.g. us-west-seattle"
                />
              </FormField>
              <FormField label="Zone Kind">
                <input
                  className={inputCls}
                  value={zoneForm.zoneKind}
                  onChange={e => setZoneForm(c => ({ ...c, zoneKind: e.target.value }))}
                />
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'source' && (
            <VerticalStack gap={4}>
              <FormField label="Source System">
                <input
                  className={inputCls}
                  value={zoneForm.sourceSystem}
                  onChange={e => setZoneForm(c => ({ ...c, sourceSystem: e.target.value }))}
                  placeholder="e.g. google_maps, internal"
                />
              </FormField>
              <FormField label="Source Reference">
                <input
                  className={inputCls}
                  value={zoneForm.sourceRef}
                  onChange={e => setZoneForm(c => ({ ...c, sourceRef: e.target.value }))}
                  placeholder="External ID or reference string"
                />
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'geometry' && (
            <VerticalStack gap={4}>
              <DescriptionText className="text-xs">
                Draw one polygon for the zone directly on the map. The saved metadata includes the polygon coordinates and GeoJSON geometry automatically.
              </DescriptionText>
              <ZoneMapEditor
                metadata={zoneParsedMetadata}
                onMetadataChange={meta => setZoneForm(c => ({ ...c, metadataJSON: JSON.stringify(meta, null, 2) }))}
                infrastructureBindings={zoneInfrastructureBindings}
              />
            </VerticalStack>
          )}

          {activeTabId === 'pricing' && (
            <VerticalStack gap={4}>
              <DescriptionText className="text-xs">
                Add optional currency, price-per-meter, and minimum-build-cost overrides by build type for route segments inside this zone. Leave the list empty to use the access-point layer defaults everywhere.
              </DescriptionText>
              <RateRows
                rates={zoneForm.buildTypeRates}
                onChange={next => setZoneForm(current => ({ ...current, buildTypeRates: next }))}
                addLabel="Add pricing override"
              />
            </VerticalStack>
          )}

          {activeTabId === 'advanced' && (
            <FormField label="Metadata (JSON)">
              <textarea
                className={textareaCls}
                value={zoneForm.metadataJSON}
                onChange={e => setZoneForm(c => ({ ...c, metadataJSON: e.target.value }))}
                spellCheck={false}
              />
            </FormField>
          )}
        </VerticalStack>
      )}

      {/* ── MARKET EDITOR ──────────────────────────────────────────────────────── */}
      {kind === 'market' && (
        <VerticalStack gap={6}>
          {activeTabId === 'identity' && (
            <VerticalStack gap={4}>
              <FormField label="Market Name">
                <input
                  className={inputCls}
                  value={marketForm.name}
                  onChange={e => setMarketForm(c => ({ ...c, name: e.target.value }))}
                  placeholder="e.g. Seattle Metro Market"
                />
              </FormField>
              <FormField label="Market Key">
                <input
                  className={inputCls}
                  value={marketForm.marketKey}
                  onChange={e => setMarketForm(c => ({ ...c, marketKey: e.target.value }))}
                  placeholder="e.g. seattle-metro"
                />
              </FormField>
              <FormField label="Serviceability Zone">
                <CommerceObjectSelector
                  options={zoneOptions}
                  value={marketForm.zoneId}
                  onChange={zoneId => setMarketForm(c => ({ ...c, zoneId }))}
                  placeholder="Select a zone"
                  searchPlaceholder="Search serviceability zones..."
                />
              </FormField>
              <FormField label="Status">
                <select
                  className={selectCls}
                  value={marketForm.status}
                  onChange={e => setMarketForm(c => ({ ...c, status: e.target.value }))}
                >
                  <option value="inactive">Inactive</option>
                  <option value="active">Active</option>
                </select>
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'readiness' && (
            <VerticalStack gap={4}>
              <FormField label="Install Method">
                <input
                  className={inputCls}
                  value={marketForm.installMethod}
                  onChange={e => setMarketForm(c => ({ ...c, installMethod: e.target.value }))}
                  placeholder="e.g. self_install, technician"
                />
              </FormField>
              <FormField label="Estimated Ready At">
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={marketForm.estimatedReadyAt}
                  onChange={e => setMarketForm(c => ({ ...c, estimatedReadyAt: e.target.value }))}
                />
              </FormField>
              <CheckToggle
                checked={marketForm.canSignUp}
                onChange={v => setMarketForm(c => ({ ...c, canSignUp: v }))}
                label="Enable Signup"
                description="Customers in this market can sign up for service."
              />
              <CheckToggle
                checked={marketForm.canScheduleInstall}
                onChange={v => setMarketForm(c => ({ ...c, canScheduleInstall: v }))}
                label="Allow Install Scheduling"
                description="Customers can book installation appointments."
              />
              <FormField label="Customer Message">
                <textarea
                  className={textareaCls}
                  value={marketForm.customerMessage}
                  onChange={e => setMarketForm(c => ({ ...c, customerMessage: e.target.value }))}
                  placeholder="Optional message shown to customers in this market..."
                />
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'groups' && (
            <VerticalStack gap={4}>
              <DescriptionText>
                Optional market group used for catalog routing and eligibility policy targeting.
              </DescriptionText>
              <FormField label="Market Group">
                <CommerceObjectSelector
                  options={marketGroupOptions}
                  value={marketForm.marketGroupId}
                  onChange={marketGroupId => setMarketForm(c => ({ ...c, marketGroupId }))}
                  placeholder="Choose a market group"
                  searchPlaceholder="Search market groups"
                  emptyMessage="No market groups found."
                />
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'advanced' && (
            <FormField label="Metadata (JSON)">
              <textarea
                className={textareaCls}
                value={marketForm.metadataJSON}
                onChange={e => setMarketForm(c => ({ ...c, metadataJSON: e.target.value }))}
                spellCheck={false}
              />
            </FormField>
          )}
        </VerticalStack>
      )}

      {/* ── MARKET GROUP EDITOR ────────────────────────────────────────────────── */}
      {kind === 'group' && (
        <VerticalStack gap={6}>
          {activeTabId === 'identity' && (
            <VerticalStack gap={4}>
              <FormField label="Group Name">
                <input
                  className={inputCls}
                  value={groupForm.name}
                  onChange={e => setGroupForm(c => ({ ...c, name: e.target.value }))}
                  placeholder="e.g. Pacific Northwest"
                />
              </FormField>
              <FormField label="Group Key">
                <input
                  className={inputCls}
                  value={groupForm.marketGroupKey}
                  onChange={e => setGroupForm(c => ({ ...c, marketGroupKey: e.target.value }))}
                  placeholder="e.g. pacific-northwest"
                />
              </FormField>
              <Surface className="border-border/60 bg-muted/10 p-4">
                <VerticalStack gap={2}>
                  <HorizontalStack className="items-center justify-between gap-3">
                    <p className="text-sm font-black text-foreground">Markets in this group</p>
                    <span className="text-xs font-bold text-muted-foreground">{groupedMarketMembers.length}</span>
                  </HorizontalStack>
                  {groupedMarketMembers.length === 0 ? (
                    <DescriptionText className="text-xs">No markets are currently assigned.</DescriptionText>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {groupedMarketMembers.map(member => (
                        <span key={member.marketId} className="inline-flex flex-col rounded-md border border-border/60 bg-background px-2.5 py-1.5">
                          <span className="text-xs font-bold text-foreground">{member.label}</span>
                          {member.description ? <span className="text-[10px] text-muted-foreground">{member.description}</span> : null}
                        </span>
                      ))}
                    </div>
                  )}
                </VerticalStack>
              </Surface>
            </VerticalStack>
          )}

          {activeTabId === 'markets' && (
            <VerticalStack gap={4}>
              <DescriptionText>
                Markets grouped together for policy and rollout targeting.
              </DescriptionText>
              <FormField label="Grouped Markets">
                <CommerceObjectMultiSelector
                  options={marketOptions}
                  values={groupForm.marketIds}
                  onChange={marketIds => setGroupForm(current => {
                    const existingLabels = new Map(current.marketIds.map((marketId, index) => [marketId, current.marketLabels?.[index]]));
                    return {
                      ...current,
                      marketIds,
                      marketLabels: marketIds.map(marketId =>
                        marketOptions.find(option => option.value === marketId)?.label || existingLabels.get(marketId) || marketId),
                    };
                  })}
                  placeholder="Add markets"
                  searchPlaceholder="Search markets"
                  emptyMessage="No markets are available."
                  noneSelectedLabel="No markets selected."
                />
              </FormField>
            </VerticalStack>
          )}

          {activeTabId === 'advanced' && (
            <FormField label="Metadata (JSON)">
              <textarea
                className={textareaCls}
                value={groupForm.metadataJSON}
                onChange={e => setGroupForm(c => ({ ...c, metadataJSON: e.target.value }))}
                spellCheck={false}
              />
            </FormField>
          )}
        </VerticalStack>
      )}
    </WizardWorkspace>
  );
}
